<script setup lang="ts">
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUiStore } from '@/stores/ui'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'

const { t, tm } = useI18n()
const ui = useUiStore()

const HELP_SECTIONS: string[] = [
    'intro',
    'navigation',
    'dashboard',
    'events',
    'ticketsets',
    'tickets',
    'devices',
    'statistics',
    'users',
    'permissions',
    'import',
    'shortcuts',
    'troubleshooting',
    'contact'
]

function pointsOf(key: string): unknown[] {
    const path: string = `help.${key}.points`
    const v = tm(path)
    return Array.isArray(v) ? v : []
}

function onSectionJump(e: Event) {
    const select = e.target as HTMLSelectElement
    const key = select.value
    if (!key) return
    const el = document.getElementById(key)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    // Reset the select to placeholder so re-selecting same key works
    select.value = ''
}

onMounted(() => {
    ui.setBreadcrumbs([{ label: t('nav.help') }])
})
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="$t('nav.help')" />

        <!-- Mobile section selector: visible only under md -->
        <div class="md:hidden mb-4">
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5 px-2">
                {{ t('help.sideNavTitle') }}
            </label>
            <select
                class="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-md text-ink"
                @change="onSectionJump"
            >
                <option value="">— {{ t('help.sectionJumpPlaceholder') }} —</option>
                <option v-for="key in HELP_SECTIONS" :key="key" :value="key">
                    {{ t(`help.sections.${key}`) }}
                </option>
            </select>
        </div>

        <div class="flex gap-6">
            <nav
                class="hidden md:block w-56 flex-shrink-0 sticky top-20 self-start max-h-[calc(100vh-6rem)] overflow-y-auto"
                aria-label="Help sections"
            >
                <div class="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 px-2">
                    {{ t('help.sideNavTitle') }}
                </div>
                <ul class="space-y-1">
                    <li v-for="key in HELP_SECTIONS" :key="key">
                        <a
                            :href="`#${key}`"
                            class="block px-2 py-1.5 rounded text-sm text-gray-700 hover:bg-gray-100 hover:text-ink transition-colors"
                        >
                            {{ t(`help.sections.${key}`) }}
                        </a>
                    </li>
                </ul>
            </nav>
            <article class="flex-1 max-w-3xl space-y-10 pb-24">
                <section v-for="key in HELP_SECTIONS" :id="key" :key="key" class="scroll-mt-24">
                    <h2 class="text-lg font-semibold text-ink mb-3">{{ t(`help.${key}.title`) }}</h2>
                    <div class="prose-sm text-gray-700 leading-relaxed space-y-2">
                        <p>{{ t(`help.${key}.intro`) }}</p>
                        <ul v-if="pointsOf(key).length" class="list-disc pl-5 space-y-1">
                            <li v-for="(_, i) in pointsOf(key)" :key="i">
                                {{ t(`help.${key}.points[${i}]`) }}
                            </li>
                        </ul>
                    </div>
                </section>
            </article>
        </div>
    </DefaultLayout>
</template>
